import { SkeletonBlock, SkeletonText } from "@zapier/design-system";
import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  isMultiSelectedAtomFamily,
  isOnlyNodeSelectedAtomFamily,
  shouldShowStepIdsAtom,
  zapStepNodesAtomFamily,
  edgesAtom,
  shouldShowAddStepButtonAtom,
  ZapNodeData,
  isViewOnlyModeAtom,
  groupNodeCreateStartAtom,
  sidebarOpenNodeIdAtom,
  loadableZapMetadataAsyncAtomFamily,
  ZapMetadata,
  isZapErrorObject,
  hasStreamingProgressStartedAtom,
  areHandlesVisibleAtomFamily,
} from "@zapier/canvas-state";
import classNames from "classnames";
import { Node, NodeProps } from "@xyflow/react";

import { AddStepButton } from "../../../../components/AddStepButton";
import { EditingGlow } from "../../../../components/EditingGlow";
import { StepId } from "../../../StepId";
import hasParent from "../../../../utils/hasParent";
import useUpdateNode from "../../../../hooks/useUpdateNode";
import { HiddenZapStep } from "../../../HiddenZapStep";
import { VerticalAnimatedLine } from "../../../VerticalAnimatedLine";
import { FloatingInlineToolbar } from "../../../FloatingInlineToolbar";
import { Handles } from "../../../Handles";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import { ZapNodeContent } from "./ZapNodeContent";
import styles from "./ZapNode.module.css";

/* IMPORTANT! We need Handles in the loading state or else the edges won't get rendered even though they exist */
function ZapNodeLoader({ id }: Pick<NodeProps<Node<ZapNodeData>>, "id">) {
  const isOnlyNodeSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const groupNodeCreateStart = useAtomValue(groupNodeCreateStartAtom);
  const sidebarOpenNodeId = useAtomValue(sidebarOpenNodeIdAtom);

  const isInEditMode =
    !isViewOnlyMode &&
    !groupNodeCreateStart &&
    isOnlyNodeSelected &&
    id === sidebarOpenNodeId;

  return (
    <>
      <div className={styles.wrapper}>
        <Handles nodeType="zap" />
        <span className={styles.zapLoaderSection}>
          <SkeletonText
            lines={1}
            size="heading"
            textAlign="left"
            width="100%"
          />
        </span>
        <SkeletonBlock borderRadius="small" height="18px" width="45px" />
      </div>
      {isInEditMode && <EditingGlow />}
    </>
  );
}

export function ZapNode({ data, id }: NodeProps<Node<ZapNodeData>>) {
  const { zapId } = data;
  const loadableData = useAtomValue(loadableZapMetadataAsyncAtomFamily(zapId));
  const fetchedZap =
    loadableData.state === "hasData" ? loadableData.data : undefined;
  const isLoadingZap = loadableData.state === "loading";
  const isZapNotFound =
    loadableData.state === "hasError" ||
    Boolean(fetchedZap && isZapErrorObject(fetchedZap));
  const zap =
    !fetchedZap || isZapNotFound ? undefined : (fetchedZap as ZapMetadata);

  const edges = useAtomValue(edgesAtom);
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const isOnlyNodeSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const shouldShowAddStepButton = useAtomValue(shouldShowAddStepButtonAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const groupNodeCreateStart = useAtomValue(groupNodeCreateStartAtom);
  const updateNodeAndSave = useUpdateNode();

  const sidebarOpenNodeId = useAtomValue(sidebarOpenNodeIdAtom);

  const setZapSteps = useSetAtom(zapStepNodesAtomFamily(id));

  const isInEditMode =
    !isViewOnlyMode &&
    !groupNodeCreateStart &&
    isOnlyNodeSelected &&
    id === sidebarOpenNodeId;
  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const isToolbarVisible =
    !isViewOnlyMode && isSelected && id !== sidebarOpenNodeId;

  const hasStreamingProgressStarted = useAtomValue(
    hasStreamingProgressStartedAtom,
  );

  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));

  useEffect(() => {
    // Need Zap to be defined since that will affect the Zap node height in order to prevent overlaps with Zap steps
    if (zap?.zap_id || isZapNotFound) {
      // Delay so that Zap node height is final before we lay out the Zap steps or else Zap steps will overlap with the Zap node
      setTimeout(setZapSteps, 0);
    }
  }, [setZapSteps, zap?.zap_id, isZapNotFound]);

  if (isLoadingZap) {
    return <ZapNodeLoader id={id} />;
  }

  return (
    <>
      {!hasParent(edges, id) && shouldShowAddStepButton && (
        <AddStepButton handleType="target" />
      )}
      {shouldShowAddStepButton && <AddStepButton />}
      <NodeHoverWrapper>
        {shouldShowStepIds && <StepId />}
        {isToolbarVisible && (
          <FloatingInlineToolbar
            showAddAppButton={false}
            actors={data.actors}
            hasZap={Boolean(zapId)}
            isZapNotFound={isZapNotFound}
            zap={zap}
          />
        )}
        <ZapNodeContent
          data={data}
          id={id}
          isSelected={isSelected}
          zap={zap}
          isZapNotFound={isZapNotFound}
          onUpdateNode={(updateData) =>
            updateNodeAndSave({ data: updateData }, { shouldSave: true })
          }
          className={classNames({
            [styles.wrapperMultiSelected]: isMultiSelected,
            [styles.wrapperSelected]: isViewOnlyMode
              ? isSelected
              : isToolbarVisible,
          })}
        >
          <Handles nodeType="zap" isVisible={areHandlesVisible} />
        </ZapNodeContent>
        {!hasStreamingProgressStarted && zapId && zap?.steps.length === 0 && (
          <>
            <VerticalAnimatedLine />
            <HiddenZapStep />
          </>
        )}
        {isInEditMode && <EditingGlow />}
      </NodeHoverWrapper>
    </>
  );
}
